import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type {
  Empresa,
  EquipmentBrand,
  SolidsDischargeType,
  ScreenType,
  BowlSize,
  ConeCountOption
} from '../../types';
import {
  empresaFormFields,
  equipmentBrandFormFields,
  dischargeTypeFormFields,
  screenTypeFormFields,
  bowlSizeFormFields,
  coneCountFormFields
} from '../../config/formFields';

interface ExcelListsSectionProps {
  data: {
    empresas: Empresa[];
    equipmentBrands: EquipmentBrand[];
    solidsDischargeTypes: SolidsDischargeType[];
    screenTypes: ScreenType[];
    bowlSizes: BowlSize[];
    coneCountOptions: ConeCountOption[];
  };
  operations: {
    handleEmpresaSubmit: (data: Record<string, any>) => Promise<void>;
    handleEmpresaToggle: (item: Empresa) => Promise<void>;
    handleEmpresaDelete: (item: Empresa) => Promise<void>;
    handleEmpresaEdit: (item: Empresa, key: string, newValue: any) => Promise<void>;
    handleEquipmentBrandSubmit: (data: Record<string, any>) => Promise<void>;
    handleEquipmentBrandToggle: (item: EquipmentBrand) => Promise<void>;
    handleEquipmentBrandDelete: (item: EquipmentBrand) => Promise<void>;
    handleEquipmentBrandEdit: (item: EquipmentBrand, key: string, newValue: any) => Promise<void>;
    handleDischargeTypeSubmit: (data: Record<string, any>) => Promise<void>;
    handleDischargeTypeToggle: (item: SolidsDischargeType) => Promise<void>;
    handleDischargeTypeDelete: (item: SolidsDischargeType) => Promise<void>;
    handleDischargeTypeEdit: (item: SolidsDischargeType, key: string, newValue: any) => Promise<void>;
    handleScreenTypeSubmit: (data: Record<string, any>) => Promise<void>;
    handleScreenTypeToggle: (item: ScreenType) => Promise<void>;
    handleScreenTypeDelete: (item: ScreenType) => Promise<void>;
    handleScreenTypeEdit: (item: ScreenType, key: string, newValue: any) => Promise<void>;
    handleEmpresasQuickSetup: () => Promise<void>;
    handleEquipmentBrandsQuickSetup: () => Promise<void>;
    handleDischargeTypesQuickSetup: () => Promise<void>;
    handleScreenTypesQuickSetup: () => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const ExcelListsSection: React.FC<ExcelListsSectionProps> = ({ data, operations, userPermissions }) => {
  // Table column configurations
  const empresaColumns: TableColumn<Empresa>[] = [
    {
      key: 'nombre',
      title: 'Nombre de la Empresa',
      width: '30%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleEmpresaEdit
    },
    {
      key: 'codigo',
      title: 'Código',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleEmpresaEdit
    },
    {
      key: 'numeroContrato',
      title: 'Nº Contrato',
      width: '25%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleEmpresaEdit,
      render: (value) => value || '(Sin contrato)'
    }
  ];

  const equipmentBrandColumns: TableColumn<EquipmentBrand>[] = [
    {
      key: 'equipmentType',
      title: 'Tipo Equipo',
      width: '18%',
      editable: true,
      editType: 'select',
      selectOptions: [
        { value: 'zaranda', label: 'Zaranda' },
        { value: 'desarenador', label: 'Desarenador' },
        { value: 'desilter', label: 'Desilter' },
        { value: 'secadora', label: 'Secadora' },
        { value: 'decanter', label: 'Decanter' }
      ],
      onEdit: operations.handleEquipmentBrandEdit
    },
    {
      key: 'brand',
      title: 'Marca',
      width: '25%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleEquipmentBrandEdit
    },
    {
      key: 'model',
      title: 'Modelo',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleEquipmentBrandEdit,
      render: (value) => value || '(Sin modelo)'
    }
  ];

  const dischargeTypeColumns: TableColumn<SolidsDischargeType>[] = [
    {
      key: 'type',
      title: 'Tipo',
      width: '30%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleDischargeTypeEdit
    },
    {
      key: 'description',
      title: 'Descripción',
      width: '50%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleDischargeTypeEdit,
      render: (value) => value || '(Sin descripción)'
    }
  ];

  const screenTypeColumns: TableColumn<ScreenType>[] = [
    {
      key: 'mesh',
      title: 'Mesh',
      width: '15%',
      editable: true,
      editType: 'number',
      onEdit: operations.handleScreenTypeEdit
    },
    {
      key: 'description',
      title: 'Descripción',
      width: '40%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleScreenTypeEdit
    },
    {
      key: 'equipmentApplicable',
      title: 'Equipos',
      width: '25%',
      render: (value) => value?.join(', ') || 'Todos'
    }
  ];

  // Master Quick Setup Handler
  const handleAllListsQuickSetup = async () => {
    if (window.confirm('¿Cargar todas las listas Excel con datos estándar? Esta acción agregará múltiples registros.')) {
      try {
        await operations.handleEmpresasQuickSetup();
        await operations.handleEquipmentBrandsQuickSetup();
        await operations.handleDischargeTypesQuickSetup();
        await operations.handleScreenTypesQuickSetup();
        alert('✅ Sistema configurado exitosamente con todas las listas Excel estándar');
      } catch (error) {
        console.error('Error en configuración completa:', error);
        alert('❌ Error durante la configuración. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>📋 Listas Hardcodeadas Excel</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Gestiona las listas estáticas utilizadas en los reportes Excel: empresas, marcas de equipos, tipos de descarga, y mallas de zarandas
        </p>
      </div>

      {/* Empresas Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Empresas',
          '🏢',
          data.empresas,
          empresaFormFields,
          empresaColumns,
          operations.handleEmpresaSubmit,
          operations.handleEmpresaToggle,
          operations.handleEmpresaDelete,
          {
            description: 'Gestiona las empresas operadoras que trabajan con el sistema de control de sólidos',
            quickSetup: {
              title: 'Carga Rápida de Empresas',
              description: 'Carga las empresas operadoras estándar (MARBAR, TECPETROL, YPF, SHELL, TOTAL AUSTRAL, WINTERSHALL)',
              buttonLabel: '🏢 Cargar Empresas Estándar',
              onQuickSetup: operations.handleEmpresasQuickSetup
            }
          }
        )}
      />

      {/* Equipment Brands Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Marcas de Equipos',
          '🏭',
          data.equipmentBrands,
          equipmentBrandFormFields,
          equipmentBrandColumns,
          operations.handleEquipmentBrandSubmit,
          operations.handleEquipmentBrandToggle,
          operations.handleEquipmentBrandDelete,
          {
            description: 'Gestiona las marcas y modelos de equipos utilizados en las operaciones de control de sólidos',
            quickSetup: {
              title: 'Carga Rápida de Marcas',
              description: 'Carga las marcas industriales estándar (SWACO, DERRICK, ALFA LAVAL) con modelos específicos',
              buttonLabel: '🏭 Cargar Marcas Estándar',
              onQuickSetup: operations.handleEquipmentBrandsQuickSetup
            }
          }
        )}
      />

      {/* Solids Discharge Types Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Tipos de Descarga de Sólidos',
          '⚡',
          data.solidsDischargeTypes,
          dischargeTypeFormFields,
          dischargeTypeColumns,
          operations.handleDischargeTypeSubmit,
          operations.handleDischargeTypeToggle,
          operations.handleDischargeTypeDelete,
          {
            description: 'Define los métodos de descarga de sólidos utilizados en los diferentes equipos de control',
            quickSetup: {
              title: 'Carga Rápida de Tipos de Descarga',
              description: 'Carga los tipos estándar: Contenedor (Zarandas, Secadoras, Decanters) y Secadora (Desarenador, Desilter)',
              buttonLabel: '⚡ Cargar Tipos Estándar',
              onQuickSetup: operations.handleDischargeTypesQuickSetup
            }
          }
        )}
      />

      {/* Screen Types Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Tipos de Malla',
          '🔍',
          data.screenTypes,
          screenTypeFormFields,
          screenTypeColumns,
          operations.handleScreenTypeSubmit,
          operations.handleScreenTypeToggle,
          operations.handleScreenTypeDelete,
          {
            description: 'Configura los tipos de malla utilizados en zarandas siguiendo estándares API RP 13C',
            quickSetup: {
              title: 'Carga Rápida de Mallas API',
              description: 'Carga mallas estándar API RP 13C (40, 60, 80, 100, 120, 150, 200 mesh)',
              buttonLabel: '🔍 Cargar Mallas API Estándar',
              onQuickSetup: operations.handleScreenTypesQuickSetup
            }
          }
        )}
      />

      {/* Master Quick Setup */}
      <div style={{
        marginTop: '40px',
        padding: '25px',
        backgroundColor: '#e3f2fd',
        borderRadius: '12px',
        border: '2px solid #2196F3'
      }}>
        <h3 style={{ color: '#1565C0', marginBottom: '15px' }}>🚀 Configuración Completa</h3>
        <p style={{
          margin: '15px 0',
          fontSize: '16px',
          color: '#424242',
          lineHeight: '1.5'
        }}>
          ¿Primera vez configurando el sistema? Carga todas las listas Excel de una vez con datos
          industriales estándar listos para usar en reportes de perforación.
        </p>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          marginTop: '20px'
        }}>
          <button
            onClick={handleAllListsQuickSetup}
            disabled={!userPermissions.canEditReports}
            style={{
              padding: '15px 30px',
              backgroundColor: userPermissions.canEditReports ? '#2196F3' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: userPermissions.canEditReports ? 'pointer' : 'not-allowed',
              fontWeight: 'bold',
              boxShadow: userPermissions.canEditReports ? '0 4px 8px rgba(33, 150, 243, 0.3)' : 'none'
            }}
          >
            📋 Configurar Todo el Sistema
          </button>

          <div style={{ fontSize: '14px', color: '#666', maxWidth: '400px' }}>
            <strong>Incluye:</strong><br/>
            • 6 empresas operadoras<br/>
            • 6 marcas industriales con modelos<br/>
            • 4 tipos de descarga estándar<br/>
            • 7 mallas API RP 13C certificadas
          </div>
        </div>
      </div>
    </>
  );
};